import React, { useEffect, useState } from "react";
import "./ApplicationForm.css";
import Swal from "sweetalert2";

const ApplicationForm = ({ selectedPosition = "", onSuccess }) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    position: selectedPosition,
    experience: "",
    qualification: "",
    passingYear: "",
    message: "",
    resume: null,
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const mobileRegex = /^[6-9]\d{9}$/;
  const currentYear = new Date().getFullYear();

  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      position: selectedPosition,
    }));
  }, [selectedPosition]);

  const handleChange = (e) => {
    if (isSubmitting) return;

    const { name, value, files } = e.target;

    if (name === "resume") {
      const file = files[0];

      if (file && file.type !== "application/pdf") {
        setErrors((prev) => ({
          ...prev,
          resume: "Only PDF files are allowed",
        }));

        setFormData((prev) => ({
          ...prev,
          resume: null,
        }));
      } else {
        setFormData((prev) => ({
          ...prev,
          resume: file,
        }));

        setErrors((prev) => ({
          ...prev,
          resume: "",
        }));
      }
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));

      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validate = () => {
    const tempErrors = {};

    if (!formData.name.trim()) {
      tempErrors.name = "Name is required";
    }

    if (!formData.email.trim()) {
      tempErrors.email = "Email is required";
    } else if (!emailRegex.test(formData.email)) {
      tempErrors.email = "Invalid email format";
    }

    if (!formData.phone.trim()) {
      tempErrors.phone = "Phone number is required";
    } else if (!mobileRegex.test(formData.phone)) {
      tempErrors.phone = "Invalid phone number";
    }

    if (!formData.position.trim()) {
      tempErrors.position = "Position is required";
    }

    if (!formData.experience.trim()) {
      tempErrors.experience = "Experience is required";
    } else if (isNaN(formData.experience)) {
      tempErrors.experience = "Experience must be a number";
    } else if (Number(formData.experience) < 0) {
      tempErrors.experience = "Experience cannot be negative";
    }

    if (!formData.qualification.trim()) {
      tempErrors.qualification = "Qualification is required";
    }

    if (!formData.passingYear.trim()) {
      tempErrors.passingYear = "Passing year is required";
    } else if (!/^\d{4}$/.test(formData.passingYear)) {
      tempErrors.passingYear = "Year must be a 4-digit number";
    } else if (
      Number(formData.passingYear) < 2000 ||
      Number(formData.passingYear) > currentYear + 1
    ) {
      tempErrors.passingYear = `Year must be between 2000 and ${
        currentYear + 1
      }`;
    }

    if (!formData.resume) {
      tempErrors.resume = "Resume (PDF) is required";
    }

    setErrors(tempErrors);

    return Object.keys(tempErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isSubmitting) return;

    if (!validate()) return;

    setIsSubmitting(true);

    Swal.fire({
      title: "Submitting Application",
      html: `
        <div style="margin-top:10px;">
          Please wait while we upload your resume and submit your application.
        </div>
      `,
      allowOutsideClick: false,
      allowEscapeKey: false,
      showConfirmButton: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    try {
      const form = new FormData();

      Object.entries(formData).forEach(([key, value]) => {
        form.append(key, value);
      });

      const response = await fetch(
        "https://node-mailer-1-lqsc.onrender.com/career",
        {
          method: "POST",
          body: form,
        }
      );

      const result = await response.json();

      console.log("Status:", response.status);
      console.log("Result:", result);

      Swal.close();

      if (result.success) {
        Swal.fire({
          icon: "success",
          title: "Application Submitted!",
          text: "Our HR Team will reach you shortly.",
          confirmButtonColor: "#3085d6",
        });

        setFormData({
          name: "",
          email: "",
          phone: "",
          position: selectedPosition,
          experience: "",
          qualification: "",
          passingYear: "",
          message: "",
          resume: null,
        });

        setErrors({});

        // Clear file input
        const fileInput = document.querySelector(
          'input[name="resume"]'
        );
        if (fileInput) {
          fileInput.value = "";
        }

        if (onSuccess) {
          onSuccess();
        }
      } else {
        Swal.fire({
          icon: "error",
          title: "Failed to Submit",
          text: result.message || "Something went wrong!",
        });
      }
    } catch (err) {
      console.error(err);

      Swal.fire({
        icon: "error",
        title: "Submission Error",
        text:
          "We are running into some technical issue. Please mail your resume to hr@panoramasoftwares.com",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {isSubmitting && (
        <div className="form-loader">
          <div className="loader-box">
            <div className="loader-spinner"></div>
            <h3>Submitting Application</h3>
            <p>Please wait while we upload your resume.</p>
            <small>This may take a few moments.</small>
          </div>
        </div>
      )}

      <div className="contact-form">
        <h2 className="form-title">
          Apply for {selectedPosition}
        </h2>

        <form onSubmit={handleSubmit}>
          <div>
            <input
              type="text"
              name="name"
              placeholder="Full Name"
              value={formData.name}
              onChange={handleChange}
              disabled={isSubmitting}
              required
            />
            {errors.name && (
              <span className="error">{errors.name}</span>
            )}
          </div>

          <div>
            <input
              type="email"
              name="email"
              placeholder="Email Address"
              value={formData.email}
              onChange={handleChange}
              disabled={isSubmitting}
              required
            />
            {errors.email && (
              <span className="error">{errors.email}</span>
            )}
          </div>

          <div>
            <input
              type="tel"
              name="phone"
              placeholder="Phone Number"
              value={formData.phone}
              onChange={handleChange}
              disabled={isSubmitting}
              required
            />
            {errors.phone && (
              <span className="error">{errors.phone}</span>
            )}
          </div>

          <div>
            <input
              type="text"
              name="position"
              value={formData.position}
              disabled
              readOnly
            />
          </div>

          <div>
            <input
              type="text"
              name="experience"
              placeholder="Experience (in years)"
              value={formData.experience}
              onChange={handleChange}
              disabled={isSubmitting}
              required
            />
            {errors.experience && (
              <span className="error">
                {errors.experience}
              </span>
            )}
          </div>

          <div>
            <input
              type="text"
              name="qualification"
              placeholder="Qualification"
              value={formData.qualification}
              onChange={handleChange}
              disabled={isSubmitting}
              required
            />
            {errors.qualification && (
              <span className="error">
                {errors.qualification}
              </span>
            )}
          </div>

          <div>
            <input
              type="text"
              name="passingYear"
              placeholder="Year of Passing"
              value={formData.passingYear}
              onChange={handleChange}
              disabled={isSubmitting}
              required
            />
            {errors.passingYear && (
              <span className="error">
                {errors.passingYear}
              </span>
            )}
          </div>

          <div>
            <textarea
              name="message"
              placeholder="Write a message (80-100 words)"
              rows="4"
              value={formData.message}
              onChange={handleChange}
              disabled={isSubmitting}
              required
            />
          </div>

          <div>
            <input
              type="file"
              name="resume"
              className="resume-upload"
              accept=".pdf"
              onChange={handleChange}
              disabled={isSubmitting}
              required
            />
            {errors.resume && (
              <span className="error">
                {errors.resume}
              </span>
            )}
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className={isSubmitting ? "loading-btn" : ""}
          >
            {isSubmitting ? (
              <>
                <span className="spinner"></span>
                Submitting...
              </>
            ) : (
              "Submit Application"
            )}
          </button>
        </form>
      </div>
    </>
  );
};

export default ApplicationForm;
